import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  arrayOf, bool, func, objectOf, oneOf, number, shape, string
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { InstantCheckout } from '@thd-olt-component-react/instant-checkout';
import { Sticky } from '@thd-olt-component-react/sticky';
import { Quantity } from '@thd-olt-component-react/quantity';
import { BuyboxAddToCart } from './BuyboxAddToCart';
import { Paypal } from './Paypal';
import {
  getCartOptions,
  getCartReqParams,
  shouldDisplaySubbuybox
} from '../../BuyboxHelper';

import '../buybox.scss';

const BuyboxActions = ({
  alterBrowserHistory,
  disableATC,
  fulfillment,
  fulfillmentMethod,
  hideInstantCheckout,
  hideQuantity,
  isAssembleItemAtStore,
  itemId,
  minimumOrderQuantity,
  onATCClick,
  onQuantityChange,
  paypalEligible,
  productAddOns = {},
  quantity,
  quantityLimitExceed,
  hidePaypalCheckout,
  sticky,
  storeId,
  storeZip,
  zipCode
}) => {
  const { channel } = useContext(ExperienceContext);

  const buyboxActionsRef = useRef(null);

  const quantityNode = !hideQuantity && (
    <div className="buybox__quantity--wrapper">
      <Quantity
        className={quantityLimitExceed ? 'buybox__quantity--warning' : ''}
        minimumQuantity={minimumOrderQuantity || 1}
        onChange={onQuantityChange}
        value={quantity}
      />
    </div>
  );

  const addToCartNode = (
    <BuyboxAddToCart
      alterBrowserHistory={alterBrowserHistory}
      disableATC={disableATC}
      fulfillment={fulfillment}
      fulfillmentMethod={fulfillmentMethod}
      isAssembleItemAtStore={isAssembleItemAtStore}
      itemId={itemId}
      onATCClick={onATCClick}
      productAddOns={productAddOns}
      quantity={quantity}
      quantityLimitExceed={quantityLimitExceed}
      storeId={storeId}
      storeZip={storeZip}
      zipCode={zipCode}
    />
  );

  return (
    <>
      {sticky && channel === 'mobile' && (
        <Sticky
          boxShadow
          className="buybox__actions buybox__actions--sticky"
          childRef={buyboxActionsRef}
          forwardRef={buyboxActionsRef}
          offset={18}
          position="bottom"
          stickyByDefault
          stickyType="forwardRef"
          visibleByDefault
        >
          <div className="buybox__actions--flex">
            {quantityNode}
            {addToCartNode}
          </div>
        </Sticky>
      )}
      <div className="buybox__actions">
        <div className="buybox__actions--flex">
          {quantityNode}
          <div className="buybox__actions--atc">
            <div ref={buyboxActionsRef}>
              {addToCartNode}
            </div>
            {(!disableATC && !quantityLimitExceed)
            && !hideInstantCheckout && (
              <InstantCheckout
                fulfillmentMethod={fulfillmentMethod}
                itemId={itemId}
                quantity={quantity}
                productAddOns={productAddOns}
              />
            )}
            {shouldDisplaySubbuybox({
              fulfillment,
              fulfillmentMethod
            }) && paypalEligible && !hidePaypalCheckout && (
              <Paypal
                cartOptions={getCartOptions({
                  channel,
                  fulfillmentMethod
                })}
                cartReqParams={getCartReqParams({
                  itemId,
                  fulfillment,
                  fulfillmentMethod,
                  productAddOns,
                  quantity,
                  storeId,
                  storeZip,
                  zipCode
                })}
                channel={channel}
                disabled={disableATC || quantityLimitExceed}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

BuyboxActions.propTypes = {
  alterBrowserHistory: bool,
  disableATC: bool,
  fulfillment: shape({
    type: string,
    locations: arrayOf(shape({
      isAnchor: bool,
      inventory: shape({
        quantity: number,
        isInStock: bool,
        isLimitedQuantity: bool,
        isBackordered: bool
      })
    }))
  }),
  fulfillmentMethod: string,
  hideInstantCheckout: bool,
  hideQuantity: bool,
  isAssembleItemAtStore: bool,
  itemId: string.isRequired,
  minimumOrderQuantity: number,
  onATCClick: func,
  onQuantityChange: func,
  productAddOns: shape({
    attachedLabor: arrayOf(shape({
      attachedLaborSku: string
    })),
    warrantyItem: shape({
      itemId: string
    })
  }),
  paypalEligible: bool,
  quantity: number,
  quantityLimitExceed: bool,
  hidePaypalCheckout: bool,
  sticky: bool,
  storeId: string,
  storeZip: string,
  zipCode: string
};

BuyboxActions.defaultProps = {
  alterBrowserHistory: false,
  disableATC: false,
  fulfillment: null,
  fulfillmentMethod: '',
  hideInstantCheckout: false,
  hideQuantity: false,
  isAssembleItemAtStore: null,
  minimumOrderQuantity: 1,
  onATCClick: () => {},
  onQuantityChange: () => {},
  paypalEligible: false,
  productAddOns: null,
  quantity: 1,
  quantityLimitExceed: false,
  hidePaypalCheckout: false,
  sticky: false,
  storeId: null,
  storeZip: null,
  zipCode: null
};

export { BuyboxActions };
